import { of as observableOf } from 'rxjs';
import { Component, ViewChild, ViewEncapsulation, Output, EventEmitter, ElementRef } from '@angular/core';
import { StageComponent, CoreShapeComponent } from 'ng2-konva';
import { Group } from 'konva/lib/Group';
import { Layer } from 'konva/lib/Layer';
import { Line } from 'konva/lib/shapes/Line';
import { Rect } from 'konva/lib/shapes/Rect';
import { Stage } from 'konva/lib/Stage';
import { Transformer } from 'konva/lib/shapes/Transformer';
import { CalcUtil } from '../../../commons/kymw00/calc-util.service';

import { PageTemplateModel } from '../kymw2400/vo/page-template.model';
import { SectionTemplateComponent } from './section-template.component';
import { SectionTemplateModel } from '../kymw2400/vo/section-template.model';
import { NewSectionModel } from '../kymw2400/vo/new-section.model';

@Component({
  selector: 'app-page-template-window',
  templateUrl: 'page-template-window.component.html',
  styleUrls: ['page-template-window.component.scss'],
  encapsulation: ViewEncapsulation.None
})

export class PageTemplateWindowComponent {
  @ViewChild('stage', { static: true }) _stage: StageComponent;
  /** ページを描写するレイヤー */
  @ViewChild('layerPage', { static: true }) _layerPage: CoreShapeComponent;
  /** セクションを描写するレイヤー */
  @ViewChild('layerSection', { static: true }) _layerSection: CoreShapeComponent;
  /** セクションプロキシを描写するレイヤー */
  @ViewChild('layerSectionProxy', { static: true }) _layerSectionProxy: CoreShapeComponent;
  /** セクション枠を描写するレイヤー */
  @ViewChild('layerSectionFrame', { static: true }) _layerSectionFrame: CoreShapeComponent;
  @ViewChild('canvas', { static: true }) canvas: ElementRef;

  @Output() sectionClick: EventEmitter<any> = new EventEmitter();

  /* 各種ゲッタ */
  private get stage(): Stage {
    return this._stage.getStage();
  }
  private get layerPage(): Layer {
    return this._layerPage.getStage() as Layer;
  }
  private get layerSection(): Layer {
    return this._layerSection.getStage() as Layer;
  }
  private get layerSectionProxy(): Layer {
    return this._layerSectionProxy.getStage() as Layer;
  }

  public stageWidth: number = null;
  public stageHeight: number = null;

  private pageName: string = null;
  private posX: number = null;
  private posY: number = null;
  private pageAreaWidth: number = null;
  private pageAreaHeight: number = null;

  private oldSectionW = Number(0);
  private oldSectionH = Number(0);
  private oldSectionX = Number(0);
  private oldSectionY = Number(0);

  // ページ情報
  private pageData: any = null;
  private pageID: string = null;

  // 色の定数
  private readonly LINE_COLOR: string = getComputedStyle(document.documentElement).getPropertyValue('--line-color');
  private readonly MOVE_LINE_COLOR: string = getComputedStyle(document.documentElement).getPropertyValue('--move-line-color');
  private readonly RESIZE_LINE_COLOR: string = getComputedStyle(document.documentElement).getPropertyValue('--resize-color');
  private readonly SUB_LINE_COLOR: string = getComputedStyle(document.documentElement).getPropertyValue('--sub-line-color');
  private readonly FILL_COLOR: string = getComputedStyle(document.documentElement).getPropertyValue('--fill-color');

  // 分割エリア内のグリッド分割数
  private readonly GRID_COUNT: number = Number(2);
  // 移動時背景色
  private readonly PROXY_COLOR: string = getComputedStyle(document.documentElement).getPropertyValue('--proxy-color');
  // 選択済み背景色
  private readonly SELECTED_COLOR: string = getComputedStyle(document.documentElement).getPropertyValue('--template-selected-color');
  // 未選択背景色
  private readonly NO_SELECTED_COLOR: string = getComputedStyle(document.documentElement).getPropertyValue('--template-no-selected-color');

  // 横のグリッド数
  private divW = Number(1);
  // 縦のグリッド数
  private divH = Number(1);
  // 幅
  public width = Number(0);
  // 高さ
  public height = Number(0);

  public scale = Number(100);

  // セクションリスト
  public sectionList: Array<SectionTemplateComponent> = new Array<SectionTemplateComponent>();

  // 編集可否フラグ
  private editableFlg = Boolean(false);
  // セクション衝突フラグ
  private collisionFlg = Boolean(false);

  /**
   * Creates an instance of PageTemplateWindowComponent.
   * @memberof PageTemplateWindowComponent
   */
  constructor(
    private elementRef: ElementRef,
  ) { }

  configStage = observableOf({
    width: this.stageWidth,
    height: this.stageHeight,
  });

  configLayerPage = observableOf({
    name: 'layerPage',
  });

  configLayerSection = observableOf({
    name: 'layerSection',
  });

  configLayerSectionProxy = observableOf({
    name: 'layerSectionProxy',
  });

  configLayerSectionFrame = observableOf({
    name: 'layerSectionFrame',
  });

  // グリッド
  private pageGrid = {
    positionX: 0,
    positionY: 0,
    sizeW: 0,
    sizeH: 0,
    divX: 0,
    divY: 0,
  };

  /**
   * 選択セクションを設定
   *
   * @param {string} sectionId セクションID
   * @memberof PageTemplateWindowComponent
   */
  public setSelectedSection(sectionTemplate: SectionTemplateComponent): void {
    this.sectionList.forEach(section => {
      if (section === sectionTemplate) {
        section.selected = true;
      } else {
        section.selected = false;
      }
    });
  }

  /**
   * 空いている場所に新しいセクションを追加
   *
   * @param {NewSectionModel} vo セクション追加画面で入力した追加するセクションの情報
   * @returns {SectionTemplateComponent}
   * @memberof PageTemplateWindowComponent
   */
  public createEmptySection(vo: NewSectionModel): SectionTemplateComponent {
    // 左上から右下にかけて空き領域がないかチェックする
    for (let y = 0; y < this.divH; y++) {
      for (let x = 0; x < this.divW; x++) {
        let collisionFlag = false;
        const newPosition: any = {
          top: y,
          left: x,
          bottom: y + 1,
          right: x + 1,
        };
        // 既存のセクションが存在するかどうかのチェック
        for (const sectionTemplate of this.sectionList) {
          // 既存のセクションが存在する場合、次の領域のチェックに移る
          if (this.isCollision(sectionTemplate.getPosition(this.divW, this.divH), newPosition)) {
            collisionFlag = true;
            break;
          }
        }
        // 開き領域だった場合、その場所にセクションテンプレートを新規作成し、作成したセクションテンプレート情報を返す
        if (!collisionFlag) {
          const sectionData: Object = {
            SectionTemplateID: [''],
            SectionTemplateName: [vo.sectionName],
            PositionX: [newPosition.left / this.divW],
            PositionY: [newPosition.top / this.divH],
            Width: [1 / this.divW],
            Height: [1 / this.divH],
            DivW: [vo.divW],
            DivH: [vo.divH]
          };

          const section: SectionTemplateComponent = new SectionTemplateComponent();
          section.page = this;
          section.setData(sectionData);
          return section;
        }
      }
    }
    // 開き領域が無い場合、nullを返す
    return null;
  }

  /**
   * 二つの矩形(セクションテンプレート)の衝突判定を行う
   *
   * @private
   * @param {*} a 衝突判定対象領域1
   * @param {*} b 衝突判定対象領域2
   * @returns
   * @memberof PageTemplateWindowComponent
   */
  private isCollision(a: any, b: any) {
    if ((a.left < b.right)
      && (a.right > b.left)
      && (a.top < b.bottom)
      && (a.bottom > b.top)) {
      return true;  // 衝突する場合
    }
    return false;   // 衝突しない場合
  }

  /**
   * 編集可能フラグ設定
   *
   * @memberof PageTemplateWindowComponent
   */
  public set editable(flag: boolean) {
    this.editableFlg = flag;
    // ドラッグ制御
    const sectionProxy = this.layerSectionProxy.getChildren(function (node) {
      return node.name() === 'sectionProxyGrp';
    });
    for (let i = Number(0); i < sectionProxy.length; i++) {
      sectionProxy[i].draggable(flag);
    }
    // リサイズ制御
    const resizeProxy = this.layerSectionProxy.getChildren(function (node) {
      return node.name() === 'sectionProxyTrans';
    });
    for (let i = Number(0); i < resizeProxy.length; i++) {
      (resizeProxy[i] as any).resizeEnabled(flag);
    }
    // セクションを再描画
    this.layerSectionProxy.draw();
  }

  /**
   * サービスから取得した値をデータにマッピングする
   *
   * @param {*} data 取得したスキーマ情報
   * @returns {void}
   * @memberof PageTemplateWindowComponent
   */
  public setData(data: any): void {
    this.pageData = data;

    // ページデータがない場合処理しない
    if (this.pageData == null) {
      return;
    }

    // ページ情報取得
    this.pageID = this.pageData.PageTemplateID;
    this.pageName = this.pageData.PageTemplateName;
    this.width = Number(this.pageData.PageAreaWidth);
    this.height = Number(this.pageData.PageAreaHeight);
    this.pageAreaWidth = Number(this.pageData.PageAreaWidth);
    this.pageAreaHeight = Number(this.pageData.PageAreaHeight);

    this.divW = Number(this.pageData.DivW);
    this.divH = Number(this.pageData.DivH);

    this.posX = Number(this.pageData.PageAreaX);
    this.posY = Number(this.pageData.PageAreaY);

    this.sectionList.length = 0;

    // セクションデータを設定
    if (this.pageData.SectionTemplateList != null && this.pageData.SectionTemplateList.SectionTemplate != null) {
      this.pageData.SectionTemplateList.SectionTemplate.forEach(section => {
        const sectionInstance: SectionTemplateComponent = new SectionTemplateComponent();
        sectionInstance.page = this;
        sectionInstance.setData(section);
        this.sectionList.push(sectionInstance);
      });
    }

    // ページ倍率設定
    this.justScale();
    // 描く
    this.reDraw();
  }

  /**
   * ページ情報をクリアする
   *
   * @memberof PageTemplateWindowComponent
   */
  public clear(): void {
    // ページ
    this.layerPage.clear();
    this.layerPage.removeChildren();
    this.layerPage.draw();
    // セクション
    this.layerSection.clear();
    this.layerSection.removeChildren();
    this.layerSection.draw();
    // セクションプロキシ
    this.layerSectionProxy.clear();
    this.layerSectionProxy.removeChildren();
    this.layerSectionProxy.draw();
  }

  /**
   * 線の再描画
   *
   * @memberof PageTemplateWindowComponent
   */
  public reDraw(): void {
    // ページクリア
    this.layerPage.clear();
    this.layerPage.removeChildren();
    this.layerPage.draw();

    // ページデータからグリッド情報を設定
    this.pageGrid = {
      sizeW: Number(this.width * this.scale / 100),
      sizeH: Number(this.height * this.scale / 100),
      divX: this.divW,
      divY: this.divH,
      positionX: this.posX,
      positionY: this.posY,
    };

    // ページの表示倍率を変更する
    this.stage.width(this.pageGrid.sizeW);
    this.stage.height(this.pageGrid.sizeH);

    // グリッドの背景
    const pageRect: Rect = new Rect({
      name: 'pageRect',
      x: this.pageGrid.positionX,
      y: this.pageGrid.positionY,
      width: this.pageGrid.sizeW,
      height: this.pageGrid.sizeH,
      fill: this.FILL_COLOR,
      draggable: false,
      stroke: this.LINE_COLOR,
      strokeWidth: 3,
    });
    this.layerPage.add(pageRect);

    // 縦線
    let gridSpace: number = this.pageGrid.sizeW / this.pageGrid.divX / this.GRID_COUNT;
    let gridCnt: number;
    for (gridCnt = 1; gridCnt < this.pageGrid.divX * this.GRID_COUNT; gridCnt++) {
      let stWidth: number;
      let stroke: string;
      stWidth = (gridCnt % this.GRID_COUNT === 0) ? 1 : 0.5;
      stroke = (gridCnt % this.GRID_COUNT === 0) ? this.LINE_COLOR : this.SUB_LINE_COLOR;
      const koLine: Line = new Line({
        name: 'pageLine',
        points: [gridCnt * gridSpace, 0, gridCnt * gridSpace, this.pageGrid.sizeH],
        stroke: stroke,
        strokeWidth: stWidth,
      });
      this.layerPage.add(koLine);
    }

    // 横線
    gridSpace = this.pageGrid.sizeH / this.pageGrid.divY / this.GRID_COUNT;
    for (gridCnt = 1; gridCnt < this.pageGrid.divY * this.GRID_COUNT; gridCnt++) {
      const koLine: Line = new Line({
        name: 'pageLine',
        points: [0, gridCnt * gridSpace, this.pageGrid.sizeW, gridCnt * gridSpace],
        stroke: (gridCnt % this.GRID_COUNT === 0) ? this.LINE_COLOR : this.SUB_LINE_COLOR,
        strokeWidth: (gridCnt % this.GRID_COUNT === 0) ? 1 : 0.5,
      });
      this.layerPage.add(koLine);
    }
    this.layerPage.draw();

    // セクションクリア
    this.layerSection.clear();
    this.layerSection.removeChildren();
    this.layerSection.draw();

    // セクションプロキシクリア
    this.layerSectionProxy.clear();
    this.layerSectionProxy.removeChildren();
    this.layerSectionProxy.draw();

    // セクション情報追加
    this.sectionList.forEach((section) => {
      this.showSectionGrid(section);
    });
  }

  /**
   * セクショングリッドを表示する
   *
   * @private
   * @param {SectionTemplateComponent} section
   * @memberof PageTemplateWindowComponent
   */
  private showSectionGrid(section: SectionTemplateComponent): void {
    // 実数値に変換
    section.drawSectionPositionX = this.pageGrid.positionX + (section.positionX * this.pageGrid.sizeW);
    section.drawSectionPositionY = this.pageGrid.positionY + (section.positionY * this.pageGrid.sizeH);
    section.drawSectionSizeW = section.sizeW * this.pageGrid.sizeW;
    section.drawSectionSizeH = section.sizeH * this.pageGrid.sizeH;

    // セクションのグループ
    const sectionGroup: Group = new Group({
      id: section.sectionId,
      name: 'sectionGroup',
      x: section.drawSectionPositionX,
      y: section.drawSectionPositionY,
      width: section.drawSectionSizeW,
      height: section.drawSectionSizeH,
    });
    sectionGroup.setAttr('sectionTemplate', section);

    // グリッドの背景
    const sectionRect: Rect = new Rect({
      name: 'sectionRect',
      x: 0,
      y: 0,
      width: section.drawSectionSizeW,
      height: section.drawSectionSizeH,
      fill: this.NO_SELECTED_COLOR,
      draggable: false,
      stroke: this.LINE_COLOR,
      strokeWidth: 3,
    });
    sectionRect.setAttr('sectionTemplate', section);
    sectionGroup.add(sectionRect);

    // 縦線
    let gridSpace: number = section.drawSectionSizeW / section.divX / 2;
    let gridCnt: number;
    for (gridCnt = 1; gridCnt < section.divX * 2; gridCnt++) {
      let stWidth: number;
      let stroke: string;
      stWidth = (gridCnt % 2 === 0) ? 1 : 0.5;
      stroke = (gridCnt % 2 === 0) ? this.LINE_COLOR : this.SUB_LINE_COLOR;
      const sectionLineV: Line = new Line({
        name: 'sectionLine',
        points: [gridCnt * gridSpace, 0, gridCnt * gridSpace, section.drawSectionSizeH],
        stroke: stroke,
        strokeWidth: stWidth,
      });
      sectionGroup.add(sectionLineV);
    }

    // 横線
    gridSpace = section.drawSectionSizeH / Number(section.divY) / 2;
    for (gridCnt = 1; gridCnt < Number(section.divY) * 2; gridCnt++) {
      const sectionLineH: Line = new Line({
        name: 'sectionLine',
        points: [0, gridCnt * gridSpace, section.drawSectionSizeW, gridCnt * gridSpace],
        stroke: (gridCnt % 2 === 0) ? this.LINE_COLOR : this.SUB_LINE_COLOR,
        strokeWidth: (gridCnt % 2 === 0) ? 1 : 0.5,
      });
      sectionGroup.add(sectionLineH);
    }
    this.layerSection.add(sectionGroup);
    this.layerSection.draw();

    /* セクションプロキシの作成 */
    // プロキシをまとめるグループを作成
    const sectionProxyGrp: Group = new Group({
      id: section.sectionId,
      name: 'sectionProxyGrp',
      x: section.drawSectionPositionX,
      y: section.drawSectionPositionY,
      width: section.drawSectionSizeW,
      height: section.drawSectionSizeH,
      opacity: 0,
      draggable: this.editableFlg,
    });
    sectionProxyGrp.setAttr('sectionTemplate', section);

    // プロキシにイベントを追加
    this.addSectionEvent(sectionProxyGrp, this);

    // プロキシの背景
    const sectionProxyRect: Rect = new Rect({
      name: 'sectionProxyRect',
      x: 0,
      y: 0,
      width: section.drawSectionSizeW,
      height: section.drawSectionSizeH,
      fill: this.FILL_COLOR,
      stroke: this.MOVE_LINE_COLOR,
      strokeWidth: 1,
      draggable: false,
    });
    sectionProxyRect.setAttr('sectionTemplate', section);
    sectionProxyGrp.add(sectionProxyRect);

    // プロキシのサイズ変更用オブジェクト
    const sectionProxyTrans = new Transformer({
      id: section.sectionId,
      name: 'sectionProxyTrans',
      rotateEnabled: false,
      resizeEnabled: this.editableFlg,
      keepRatio: false,
      opacity: 0.6,
    });
    sectionProxyTrans.setAttr('sectionTemplate', section);

    // プロキシのサイズ変更時に表示する矩形
    const sectionProxyResizeRect = new Rect({
      id: section.sectionId,
      name: 'sectionProxyResizeRect',
      x: section.drawSectionPositionX,
      y: section.drawSectionPositionY,
      width: section.drawSectionSizeW,
      height: section.drawSectionSizeH,
      fill: this.FILL_COLOR,
      stroke: this.RESIZE_LINE_COLOR,
      strokeWidth: 1,
      opacity: 0,
      draggable: false,
    });
    this.layerSectionProxy.add(sectionProxyResizeRect);

    // プロキシにセクションサイズ変更時の枠を設定
    this.setResizeDragRange(sectionProxyTrans, sectionProxyGrp);

    // セクションサイズ変更時のイベント追加
    this.addResizeEvent(sectionProxyTrans, sectionProxyResizeRect, this);

    // プロキシを描画
    this.layerSectionProxy.add(sectionProxyGrp);
    this.layerSectionProxy.add(sectionProxyTrans);
    // TODO ↓ Konva warning: tr.setNode(shape), tr.node(shape) and tr.attachTo(shape) methods are deprecated. Please use tr.nodes(nodesArray) instead.
    sectionProxyTrans.attachTo(sectionProxyGrp);
    this.layerSectionProxy.draw();
  }

  /**
   * セクションサイズ変更のドラッグ範囲を設定
   *
   * @private
   * @param {Transformer} transformer
   * @param {Group} proxyGroup
   * @memberof PageTemplateWindowComponent
   */
  private setResizeDragRange(transformer: Transformer, proxyGroup: Group): void {
    const rect: any[] = transformer.getChildren();
    const groupWidth = proxyGroup.width() * proxyGroup.scaleX();
    const groupHeight = proxyGroup.height() * proxyGroup.scaleY();
    // セクションリサイズ用の掴める範囲を設定
    const width = groupWidth * 0.15 < 5 ? 5 : groupWidth * 0.15;
    const height = groupHeight * 0.15 < 5 ? 5 : groupHeight * 0.15;

    transformer.anchorStyleFunc((anchor) => {
      anchor.opacity(0);
      const name = anchor.name().split(' ')[0];
      switch (name) {
        case 'top-left':
          anchor.offsetX(0);
          anchor.offsetY(0);
          anchor.width(width);
          anchor.height(height);
          break;
        case 'top-center':
          anchor.offsetX((groupWidth - width * 2) / 2);
          anchor.offsetY(0);
          anchor.width(groupWidth - width * 2);
          anchor.height(height);
          break;
        case 'top-right':
          anchor.offsetX(width);
          anchor.offsetY(0);
          anchor.width(width);
          anchor.height(height);
          break;
        case 'middle-left':
          anchor.offsetX(0);
          anchor.offsetY((groupHeight - height * 2) / 2);
          anchor.width(width);
          anchor.height(groupHeight - height * 2);
          break;
        case 'middle-right':
          anchor.offsetX(width);
          anchor.offsetY((groupHeight - height * 2) / 2);
          anchor.width(width);
          anchor.height(groupHeight - height * 2);
          break;
        case 'bottom-left':
          anchor.offsetX(0);
          anchor.offsetY(height);
          anchor.width(width);
          anchor.height(height);
          break;
        case 'bottom-center':
          anchor.offsetX((groupWidth - width * 2) / 2);
          anchor.offsetY(height);
          anchor.width(groupWidth - width * 2);
          anchor.height(height);
          break;
        case 'bottom-right':
          anchor.offsetX(width);
          anchor.offsetY(height);
          anchor.width(width);
          anchor.height(height);
          break;
        case 'back':
          anchor.width(groupWidth);
          anchor.height(groupHeight);
          anchor.stroke(this.LINE_COLOR);
          anchor.fill(this.PROXY_COLOR);
          break;
      }
    });

    rect.forEach(item => {
      item.opacity(0);
      if ('back' === item.name()) {
        item.width(groupWidth);
        item.height(groupHeight);
        item.stroke(this.LINE_COLOR);
        item.fill(this.PROXY_COLOR);
      }
    });
  }

  /**
   * セクションに対してイベントを追加する
   *
   * @param {Group} sectionProxyGroup
   * @param {PageTemplateWindowComponent} pageComp
   * @memberof PageTemplateWindowComponent
   */
  private addSectionEvent(sectionProxyGroup: Group, pageComp: PageTemplateWindowComponent): void {

    // マウスダウン時イベント
    // セクションの背景色を変更する
    sectionProxyGroup.on('mousedown', function (e: any) {
      /** セクション情報のバックアップ(選択時) **/
      pageComp.backupOldSection(e.target.getParent());
      // ページに選択セクション(自分)IDを通知して他のセクションを非選択状態にする
      pageComp.setSelectedSection(e.target.getAttr('sectionTemplate'));
      // 各レイヤーを取得
      const targetSection: Group = pageComp.layerSection.getChildren(function (node) {
        return node.getAttr('sectionTemplate') === e.target.getAttr('sectionTemplate');
      })[0] as Group;

      // すべてのセクションを未選択色に設定
      pageComp.setAllSectionColor(false);
      // 選択セクションの背景色を設定
      const sectionRect: Rect = targetSection.getChildren(function (node) {
        return node.name() === 'sectionRect';
      })[0] as Rect;
      // 選択色を設定
      sectionRect.fill(pageComp.SELECTED_COLOR);

      // セクションを描画
      pageComp.layerSection.draw();

      // セクション選択イベント
      pageComp.sectionClick.emit();
    });

    // ドラッグ中イベント
    // プロキシを表示する
    sectionProxyGroup.on('dragmove', function (e: any) {
      // 編集不可の場合、処理しない
      if (!pageComp.editableFlg) {
        return;
      }
      const mouseEvent: MouseEvent = e.evt;
      const moveSectionGroup: Group = pageComp.layerSection.getChildren(function (node) {
        return node.getAttr('sectionTemplate') === e.target.getAttr('sectionTemplate');
      })[0] as Group;

      // 移動元位置を取得
      const oldPosition = pageComp.getSectionProxyPosition(
        pageComp.oldSectionX, pageComp.oldSectionY, pageComp.oldSectionW, pageComp.oldSectionH);

      // 移動元情報から移動後のセクションサイズを設定
      const targetSectionW = (pageComp.pageGrid.sizeW / pageComp.divW) * (oldPosition.right - oldPosition.left);
      const targetSectionH = (pageComp.pageGrid.sizeH / pageComp.divH) * (oldPosition.bottom - oldPosition.top);

      // 移動後の位置を取得
      const position = pageComp.getSectionProxyPosition(
        mouseEvent.offsetX - (targetSectionW / 2),
        mouseEvent.offsetY - (targetSectionH / 2),
        targetSectionW,
        targetSectionH);
      const x = (position.left * pageComp.pageGrid.sizeW) / pageComp.divW;
      const y = (position.top * pageComp.pageGrid.sizeH) / pageComp.divH;

      pageComp.collisionFlg = pageComp.isCollisionSection(pageComp.selectedSection(), position);
      // 他のセクションと重なる場合
      // ページ以外にドラッグしている場合
      if (pageComp.collisionFlg || e.evt.srcElement.localName !== 'canvas') {
        pageComp.stage.container().style.cursor = 'no-drop';
      } else {
        pageComp.stage.container().style.cursor = 'copy';
      }

      // プロキシ設定
      moveSectionGroup.opacity(0.5);
      sectionProxyGroup.opacity(0.5);
      sectionProxyGroup.x(x);
      sectionProxyGroup.y(y);

      // セクション描画
      pageComp.layerSection.draw();
      pageComp.layerSectionProxy.draw();
    });

    // ドラッグ終了時イベント
    // プロキシを非表示にし、セクションを移動後の位置に描画する
    sectionProxyGroup.on('dragend', function (e: any) {
      // 編集不可の場合、処理しない
      if (!pageComp.editableFlg) {
        return;
      }
      const moveSectionGroup: Group = pageComp.layerSection.getChildren(function (node) {
        return node.getAttr('sectionTemplate') === e.target.getAttr('sectionTemplate');
      })[0] as Group;

      // 他のセクションと重なる場合
      // ページ以外にドラッグしている場合
      if (pageComp.collisionFlg || e.evt.srcElement.localName !== 'canvas') {
        // プロキシ設定
        sectionProxyGroup.x(moveSectionGroup.x());
        sectionProxyGroup.y(moveSectionGroup.y());
      } else {
        // セクションサイズを調整
        moveSectionGroup.x(sectionProxyGroup.x());
        moveSectionGroup.y(sectionProxyGroup.y());

        // セクションデータを更新
        pageComp.updateSection(sectionProxyGroup);
      }
      // カーソルをもとに戻す
      pageComp.stage.container().style.cursor = 'auto';
      document.body.style.cursor = 'auto';

      // プロキシを透明に戻す
      sectionProxyGroup.opacity(0);
      moveSectionGroup.opacity(1);

      // セクション描画
      pageComp.layerSection.draw();
      pageComp.layerSectionProxy.draw();
      pageComp.sectionClick.emit();
    });
  }

  /**
   * セクションに対してリサイズイベントを追加する
   *
   * @private
   * @param {Transformer} transformer
   * @param {Rect} rect
   * @param {PageTemplateWindowComponent} pageComp
   * @memberof PageTemplateWindowComponent
   */
  private addResizeEvent(transformer: Transformer, rect: Rect, pageComp: PageTemplateWindowComponent): void {
    // ドラッグ開始時イベント
    // セクションの背景色を設定する
    transformer.on('mousedown', function (e: any) {
      /** セクション情報のバックアップ(選択時) **/
      pageComp.backupOldSection(e.target.getParent());
      // ページに選択セクション(自分)IDを通知して他のセクションを非選択状態にする
      pageComp.setSelectedSection(e.target.getParent().getAttr('sectionTemplate'));
      // 対象セクションを取得
      const targetSection: Group = pageComp.layerSection.getChildren(function (node) {
        return node.getAttr('sectionTemplate') === e.target.getParent().getAttr('sectionTemplate');
      })[0] as Group;

      // すべてのセクションを未選択色に設定
      pageComp.setAllSectionColor(false);
      // 選択セクションの背景色を設定
      const sectionRect: Rect = targetSection.getChildren(function (node) {
        return node.name() === 'sectionRect';
      })[0] as Rect;
      // 選択色を設定
      sectionRect.fill(pageComp.SELECTED_COLOR);

      // セクションを描画
      pageComp.layerSection.draw();

      // セクション選択イベント
      pageComp.sectionClick.emit();
    });

    transformer.on('transform', function (e: any) {
      // 編集不可の場合、処理しない
      if (!pageComp.editableFlg) {
        return;
      }
      const mouseEvent: MouseEvent = e.evt;
      const resizeSectionGroup: Group = pageComp.layerSection.getChildren(function (node) {
        return node.getAttr('sectionTemplate') === e.target.getAttr('sectionTemplate');
      })[0] as Group;
      // プロキシを半透明で表示
      rect.opacity(0.5);
      if (e.evt.srcElement.localName !== 'canvas') {
        pageComp.stage.container().style.cursor = 'no-drop';
        rect.opacity(0);
        pageComp.layerSectionProxy.draw();
        return;
      }

      // サイズ変更時の種類を取得("top-left"など)
      const transType = e.currentTarget._movingAnchorName;

      const tempSection = e.target.getAttr('sectionTemplate');

      // 吸着処理を行った場合の座標を取得
      const position = pageComp.getSectionProxyPositionForResize(
        pageComp.getMousePosition(mouseEvent.offsetX, mouseEvent.offsetY),
        tempSection,
        transType);

      // 他のセクションと重なる場合
      pageComp.collisionFlg = pageComp.isCollisionSection(pageComp.selectedSection(), position);
      // カーソル設定
      if (pageComp.collisionFlg) {
        pageComp.stage.container().style.cursor = 'no-drop';
        return;
      } else {
        pageComp.stage.container().style.cursor = 'copy';
      }

      // 矩形を描写する
      const x = CalcUtil.multiply(position.left.toString(), pageComp.pageGrid.sizeW.toString()) / pageComp.divW;
      const y = CalcUtil.multiply(position.top.toString(), pageComp.pageGrid.sizeH.toString()) / pageComp.divH;
      const width = CalcUtil.multiply(
        CalcUtil.subtract(position.right.toString(), position.left.toString()).toString(),
        pageComp.pageGrid.sizeW.toString())
        / pageComp.divW;
      const height = CalcUtil.multiply(
        CalcUtil.subtract(position.bottom.toString(), position.top.toString()).toString(),
        pageComp.pageGrid.sizeH.toString())
        / pageComp.divH;
      rect.x(x);
      rect.y(y);
      rect.width(width);
      rect.height(height);

      // 透明度設定
      resizeSectionGroup.opacity(0.5);

      // セクション描画
      pageComp.layerSection.draw();
    });

    // ドラッグ終了時イベント
    // プロキシを非表示にし、セクションのサイズを変更する
    transformer.on('transformend', function (e: any) {
      // 編集不可の場合、処理しない
      if (!pageComp.editableFlg) {
        return;
      }

      const resizeSectionProxyGroup: Group = pageComp.layerSectionProxy.getChildren(function (node) {
        return node.getAttr('sectionTemplate') === e.target.getAttr('sectionTemplate') && node.name() === 'sectionProxyGrp';
      })[0] as Group;
      const resizeSectionGroup: Group = pageComp.layerSection.getChildren(function (node) {
        return node.getAttr('sectionTemplate') === e.target.getAttr('sectionTemplate');
      })[0] as Group;

      if (pageComp.collisionFlg) {
        // プロキシ設定
        resizeSectionProxyGroup.x(resizeSectionGroup.x());
        resizeSectionProxyGroup.y(resizeSectionGroup.y());
        resizeSectionProxyGroup.width(resizeSectionGroup.width());
        resizeSectionProxyGroup.height(resizeSectionGroup.height());
        resizeSectionProxyGroup.scaleX(resizeSectionGroup.scaleX());
        resizeSectionProxyGroup.scaleY(resizeSectionGroup.scaleY());
      } else {
        // プロキシ設定
        resizeSectionProxyGroup.x(rect.x());
        resizeSectionProxyGroup.y(rect.y());
        resizeSectionProxyGroup.scaleX(rect.width() / resizeSectionGroup.width());
        resizeSectionProxyGroup.scaleY(rect.height() / resizeSectionGroup.height());

        // セクション設定
        resizeSectionGroup.x(rect.x());
        resizeSectionGroup.y(rect.y());
        resizeSectionGroup.scaleX(rect.width() / resizeSectionGroup.width());
        resizeSectionGroup.scaleY(rect.height() / resizeSectionGroup.height());

        // セクションデータを更新
        pageComp.updateSection(resizeSectionGroup);
      }
      // リサイズオブジェクトを再設定
      pageComp.setResizeDragRange(transformer, resizeSectionProxyGroup);
      transformer.attachTo(resizeSectionProxyGroup);

      // プロキシを透明に戻す
      rect.opacity(0);
      resizeSectionGroup.opacity(1);

      // セクション描画
      pageComp.layerSection.draw();
      pageComp.layerSectionProxy.draw();

      // カーソルをもとに戻す
      document.body.style.cursor = 'auto';
      pageComp.stage.container().style.cursor = 'auto';

      pageComp.sectionClick.emit();
    });
  }

  /**
   * 移動前のセクションの情報をバックアップする
   *
   * @param {Group} target
   * @memberof PageTemplateWindowComponent
   */
  private backupOldSection(target: Group) {
    this.oldSectionX = target.x();
    this.oldSectionY = target.y();
    this.oldSectionW = target.width() * target.scaleX();
    this.oldSectionH = target.height() * target.scaleY();
  }

  /**
   * すべてのセクションの背景色を未選択色に設定
   *
   * @param {boolean} drawFlag TRUE:描画する、FALSE:描画しない
   * @memberof PageTemplateWindowComponent
   */
  setAllSectionColor(drawFlag: boolean) {
    const sectionList: any[] = this.layerSection.getChildren(function (node) {
      return node.name() === 'sectionGroup';
    });
    let i = 0;
    // 対象ページのセクションの背景色を設定
    for (i = 0; i < sectionList.length; i++) {
      const sectionRect: Rect = sectionList[i].getChildren(function (node) {
        return node.name() === 'sectionRect';
      })[0];
      // 未選択セクション背景色
      sectionRect.fill(this.NO_SELECTED_COLOR);
    }

    if (drawFlag) {
      this.layerSection.draw();
    }
  }

  /**
   * 移動時(吸着処理後)のセクションプロキシの座標(ページの左上を(0,0)、右下を(X分割数,Y分割数))を取得する
   *
   * @private
   * @param {number} x 移動後のx位置
   * @param {number} y 移動後のy位置
   * @param {number} width 移動するセクションの幅
   * @param {number} height 移動するセクションの高さ
   * @returns {*}
   * @memberof PageTemplateWindowComponent
   */
  private getSectionProxyPosition(x: number, y: number, width: number, height: number): any {
    // 上と左の座標を取得する(誤差を少なくする為、グリッドの最小単位0.5を1として一旦算出)
    let top = Number(CalcUtil.round((y / (this.pageGrid.sizeH / this.divH) * this.GRID_COUNT).toString(), 2));
    let left = Number(CalcUtil.round((x / (this.pageGrid.sizeW / this.divW) * this.GRID_COUNT).toString(), 2));
    // グリッドの最小単位0.25を掛けて、本来の数値に戻す)
    top = CalcUtil.multiply(Math.floor(top).toString(), (1 / this.GRID_COUNT).toString());
    left = CalcUtil.multiply(Math.floor(left).toString(), (1 / this.GRID_COUNT).toString());
    // 上・左の値にそれぞれ幅・高さを足して下・右の座標を算出
    const bottom = CalcUtil.add(top.toString(), CalcUtil.round((height / (this.pageGrid.sizeH / this.divH)).toString(), 2));
    const right = CalcUtil.add(left.toString(), CalcUtil.round((width / (this.pageGrid.sizeW / this.divW)).toString(), 2));
    // ページ領域からはみ出さないように修正
    if (bottom > this.divH) {
      top = CalcUtil.subtract(
        top.toString(),
        CalcUtil.subtract(bottom.toString(), this.divH.toString()).toString());
    } else if (top < 0) {
      top = 0;
    }
    if (right > this.divW) {
      left = CalcUtil.subtract(
        left.toString(),
        CalcUtil.subtract(right.toString(), this.divW.toString()).toString());
    } else if (left < 0) {
      left = 0;
    }

    return {
      top: top,
      left: left,
      bottom: bottom,
      right: right,
    };
  }

  /**
   * サイズ変更時(吸着処理後)のセクションプロキシの座標(ページの左上を(0,0)、右下を(X分割数,Y分割数))を取得する
   *
   * @private
   * @param {*} mousePosition マウスポインタのあるグリッドの座標情報
   * @param {SectionTemplateComponent} section 変更対象のセクションの座標情報(変更前の座標)
   * @param {string} type type 変更の種類(top-leftなど)
   * @returns {*}
   * @memberof PageTemplateWindowComponent
   */
  private getSectionProxyPositionForResize(mousePosition: any, section: SectionTemplateComponent, type: string): any {
    // 変更前の座標値を一旦設定
    let top = section.positionY * this.divH;
    let left = section.positionX * this.divW;
    let bottom = (section.positionY + section.sizeH) * this.divH;
    let right = (section.positionX + section.sizeW) * this.divW;

    // 上下のサイズ変更
    if (type.indexOf('top') > -1) {             // 上からドラッグした場合
      if (bottom < mousePosition.bottom) {   // 上下反転した場合
        top = CalcUtil.subtract(bottom.toString(), (1 / this.GRID_COUNT).toString());  // 最小グリッド分残す
        bottom = mousePosition.bottom;
      } else {                            // 通常の操作
        top = mousePosition.top;
      }
    } else if (type.indexOf('bottom') > -1) {   // 下からドラッグした場合
      if (top > mousePosition.top) {   // 上下反転した場合
        bottom = CalcUtil.add(top.toString(), (1 / this.GRID_COUNT).toString());       // 最小グリッド分残す
        top = mousePosition.top;
      } else {                            // 通常の操作
        bottom = mousePosition.bottom;
      }
    }
    // サイズが同じ(高さが0)になる場合、グリッドの最小サイズ(0.5)は残す
    if (top === bottom) {
      if (type.indexOf('top') > -1) {
        top = CalcUtil.subtract(top.toString(), (1 / this.GRID_COUNT).toString());
      } else {
        bottom = CalcUtil.add(bottom.toString(), (1 / this.GRID_COUNT).toString());
      }
    }

    // 左右のサイズ変更
    if (type.indexOf('left') > -1) {            // 左からドラッグした場合
      if (right < mousePosition.right) {   // 反転した場合
        left = CalcUtil.subtract(right.toString(), (1 / this.GRID_COUNT).toString());
        right = mousePosition.right;
      } else {                            // 通常の操作
        left = mousePosition.left;
      }
    } else if (type.indexOf('right') > -1) {    // 右からドラッグした場合
      if (left > mousePosition.left) {   // 反転した場合
        right = CalcUtil.add(left.toString(), (1 / this.GRID_COUNT).toString());
        left = mousePosition.left;
      } else {                            // 通常の操作
        right = mousePosition.right;
      }
    }
    // サイズが同じ(幅が0)になる場合、グリッドの最小サイズ(0.5)は残す
    if (left === right) {
      if (type.indexOf('left') > -1) {
        left = CalcUtil.subtract(left.toString(), (1 / this.GRID_COUNT).toString());
      } else {
        right = CalcUtil.add(right.toString(), (1 / this.GRID_COUNT).toString());
      }
    }

    // ページ領域からはみ出さないように修正した値を返す
    return {
      top: (top < 0) ? 0 : top,
      left: (left < 0) ? 0 : left,
      bottom: (bottom > this.divH) ? this.divH : bottom,
      right: (right > this.divW) ? this.divW : right,
    };
  }

  /**
   * マウスポインタのあるグリッドの位置をページの座標で取得する（グリッド分割）
   *
   * @private
   * @param {number} x ページ上のマウスのX座標
   * @param {number} y ページ上のマウスのY座標
   * @returns
   * @memberof PageTemplateWindowComponent
   */
  private getMousePosition(x: number, y: number) {
    // 上と左の座標を取得する(誤差を少なくする為、グリッドの最小単位0.5を1として一旦算出)
    let top = Number(CalcUtil.round((y / (this.pageGrid.sizeH / this.divH) * this.GRID_COUNT).toString(), 2));
    let left = Number(CalcUtil.round((x / (this.pageGrid.sizeW / this.divW) * this.GRID_COUNT).toString(), 2));
    // グリッドの最小単位0.5を掛けて、本来の数値に戻す)
    top = CalcUtil.multiply(Math.floor(top).toString(), (1 / this.GRID_COUNT).toString());
    left = CalcUtil.multiply(Math.floor(left).toString(), (1 / this.GRID_COUNT).toString());
    // グリッドの最小単位0.5を足して下と右の座標を取得する
    const bottom = CalcUtil.add(top.toString(), (1 / this.GRID_COUNT).toString());
    const right = CalcUtil.add(left.toString(), (1 / this.GRID_COUNT).toString());
    return {
      top: (top < 0) ? 0 : top,
      left: (left < 0) ? 0 : left,
      bottom: (bottom > this.divH) ? this.divH : bottom,
      right: (right > this.divW) ? this.divW : right,
    };
  }

  /**
   * 選択されたセクションを返却する
   *
   * @returns {SectionTemplateComponent}
   * @memberof PageTemplateWindowComponent
   */
  public selectedSection(): SectionTemplateComponent {
    for (let i = Number(0); i < this.sectionList.length; i++) {
      if (this.sectionList[i].selected) {
        return this.sectionList[i];
      }
    }
    return null;
  }

  /**
   * 対象セクショングループ内のセクションに対して、衝突判定を行う
   *
   * @param {string} sectionId 衝突判定対象セクションID（新規追加時はnull）
   * @param {*} tempSectionPosition 対象セクション情報
   * @returns {boolean} true:衝突, false:衝突していない
   * @memberof PageTemplateWindowComponent
   */
  isCollisionSection(sectionTemplate: SectionTemplateComponent, tempSectionPosition: any): boolean {
    let i: number;
    let collisionFlg = Boolean(false);
    for (i = 0; i < this.sectionList.length; i++) {
      const position = this.getSectionProxyPosition(
        this.sectionList[i].drawSectionPositionX, this.sectionList[i].drawSectionPositionY,
        this.sectionList[i].drawSectionSizeW, this.sectionList[i].drawSectionSizeH);

      if (!(tempSectionPosition.left >= position.right || tempSectionPosition.right <= position.left
        || tempSectionPosition.top >= position.bottom || tempSectionPosition.bottom <= position.top)) {
        if (sectionTemplate == null || sectionTemplate !== this.sectionList[i]) {
          collisionFlg = true;
        }
      }
    }
    return collisionFlg;
  }

  /**
   * セクション情報更新
   *
   * @private
   * @param {Group} section
   * @memberof PageTemplateWindowComponent
   */
  private updateSection(section: Group): void {
    const sectionComp = this.selectedSection();
    const sectionPosition = this.getSectionProxyPosition(section.x(), section.y(),
      section.width() * section.scaleX(), section.height() * section.scaleY());
    sectionComp.positionX = sectionPosition.left / this.divW;
    sectionComp.positionY = sectionPosition.top / this.divH;
    sectionComp.sizeW = (sectionPosition.right - sectionPosition.left) / this.divW;
    sectionComp.sizeH = (sectionPosition.bottom - sectionPosition.top) / this.divH;
    sectionComp.drawSectionPositionX = this.pageGrid.positionX + (sectionComp.positionX * this.pageGrid.sizeW);
    sectionComp.drawSectionPositionY = this.pageGrid.positionY + (sectionComp.positionY * this.pageGrid.sizeH);
    sectionComp.drawSectionSizeW = sectionComp.sizeW * this.pageGrid.sizeW;
    sectionComp.drawSectionSizeH = sectionComp.sizeH * this.pageGrid.sizeH;
  }

  /**
   * PageTemplateVO作成
   *
   * @returns {PageTemplateModel}
   * @memberof PageTemplateWindowComponent
   */
  public createPageTemplateVO(): PageTemplateModel {
    const vo: PageTemplateModel = new PageTemplateModel();

    vo.pageTemplateID = this.pageID;
    vo.pageTemplateName = this.pageName;
    vo.divH = this.divH;
    vo.divW = this.divW;
    // 項番2231 START 障害管理NO.I00458対応
    // vo.pageAreaHeight = Number(this.pageAreaHeight);
    // vo.pageAreaWidth = Number(this.pageAreaWidth);
    // vo.pageAreaX = this.posX;
    // vo.pageAreaY = this.posY;
    vo.pageAreaHeight = String(this.pageAreaHeight);
    vo.pageAreaWidth = String(this.pageAreaWidth);
    vo.pageAreaX = String(this.posX);
    vo.pageAreaY = String(this.posY);
    // 項番2231 END 障害管理NO.I00458対応

    this.sectionList.forEach(newSection => {
      const sectionTemplateVO: SectionTemplateModel = newSection.createSectionTemplateVO();
      vo.addSection(this.completeSectionTemplateVO(sectionTemplateVO));
    });

    return vo;
  }

  /**
   * SectionTemplateVO作成
   *
   * @returns {SectionTemplateModel}
   * @memberof PageTemplateWindowComponent
   */
  public completeSectionTemplateVO(vo: SectionTemplateModel): SectionTemplateModel {
    // 項番2231 START 障害管理NO.U01748対応
    // vo.percentPositionX = this.getJustWidthScale(vo.positionX);
    // vo.percentPositionY = this.getJustHeightScale(vo.positionY);
    // vo.percentWidth = this.getJustWidthScale(vo.width);
    // vo.percentHeight = this.getJustHeightScale(vo.height);
    // vo.width = Number(this.width * vo.percentWidth);
    // vo.height = Number(this.height * vo.percentHeight);
    // vo.positionX = Number(this.width * vo.percentPositionX);
    // vo.positionY = Number(this.height * vo.percentPositionY);
    vo.percentPositionX = this.getJustWidthScale(Number(vo.positionX));
    vo.percentPositionY = this.getJustHeightScale(Number(vo.positionY));
    vo.percentWidth = this.getJustWidthScale(Number(vo.width));
    vo.percentHeight = this.getJustHeightScale(Number(vo.height));
    vo.width = String(this.width * vo.percentWidth);
    vo.height = String(this.height * vo.percentHeight);
    vo.positionX = String(this.width * vo.percentPositionX);
    vo.positionY = String(this.height * vo.percentPositionY);
    // 項番2231 END 障害管理NO.U01748対応

    return vo;
  }

  /**
   * 倍率取得
   *
   * @private
   * @memberof PageTemplateWindowComponent
   */
  private justScale(): void {
    const pageWidth = this.width;
    const pageHeight = this.height;

    // canvasのheightから、padding分を先に引いて計算
    const rw = Number((this.canvas.nativeElement.offsetWidth - 20) / pageWidth);
    const rh = Number((this.canvas.nativeElement.offsetHeight - 20) / pageHeight);

    let ratio: number;
    ratio = Number(Math.min(rw, rh));
    this.scale = Math.floor(ratio * 10) * 10;
  }

  /**
   * 倍率取得(幅)
   *
   * @param {number} checkWidth
   * @returns {number}
   * @memberof PageTemplateWindowComponent
   */
  public getJustWidthScale(checkWidth: number): number {
    let justWidthScale: number;
    const gridWLength: number = this.GRID_COUNT * this.divW;
    const gridWidth = Number(this.width / gridWLength);
    for (let gridWCount = Number(0); gridWCount < gridWLength; gridWCount++) {
      const leftPosition: number = gridWidth * gridWCount;
      const rightPosition: number = gridWidth * (gridWCount + 1);
      // 少数値比較時の誤差に対応
      if (leftPosition <= checkWidth && checkWidth < rightPosition + gridWidth * 0.1) {
        if (checkWidth !== 0 && Number((rightPosition - leftPosition) / (checkWidth - leftPosition)) > 0.5) {
          justWidthScale = (gridWCount + 1) / gridWLength;
        } else {
          justWidthScale = gridWCount / gridWLength;
        }
        break;
      }
    }

    return justWidthScale;
  }

  /**
   * 倍率取得(高さ)
   *
   * @param {number} checkHeight
   * @returns {number}
   * @memberof PageTemplateWindowComponent
   */
  public getJustHeightScale(checkHeight: number): number {
    let justHeightScale: number;
    const gridHLength: number = this.GRID_COUNT * this.divH;
    const gridHeight = Number(this.height / gridHLength);
    for (let gridHCount = Number(0); gridHCount < gridHLength; gridHCount++) {
      const topPosition: number = gridHeight * gridHCount;
      const bottomPosition: number = gridHeight * (gridHCount + 1);
      // 少数値比較時の誤差に対応
      if (topPosition <= checkHeight && checkHeight < bottomPosition + gridHeight * 0.1) {
        if (checkHeight !== 0 && Number((bottomPosition - topPosition) / (checkHeight - topPosition)) > 0.5) {
          justHeightScale = (gridHCount + 1) / gridHLength;
        } else {
          justHeightScale = gridHCount / gridHLength;
        }
        break;
      }
    }

    return justHeightScale;
  }
}
